import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./style.scss";

interface Props {
  children: React.ReactNode;
  content?: React.ReactNode;
  className?: string;
}

export const ReferenceTooltip: React.FC<Props> = ({
  children,
  content,
  className
}) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPosition({
      left: rect.left + rect.width / 2,
      top: rect.top
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    const handleUpdate = () => updatePosition();
    window.addEventListener("scroll", handleUpdate, true);
    window.addEventListener("resize", handleUpdate);
    return () => {
      window.removeEventListener("scroll", handleUpdate, true);
      window.removeEventListener("resize", handleUpdate);
    };
  }, [isOpen, updatePosition]);

  const canShow = content != null;
  const show = isOpen && canShow && position != null;

  const portal = (() => {
    if (!show || typeof document === "undefined" || position == null) {
      return null;
    }

    return createPortal(
      <span
        className={styles.referenceTooltipBubble}
        aria-hidden
        style={{
          left: position.left,
          top: position.top
        }}
      >
        {content}
      </span>,
      document.body
    );
  })();

  return (
    <span
      ref={triggerRef}
      className={
        className
          ? `${styles.referenceTooltip} ${className}`
          : styles.referenceTooltip
      }
      onMouseEnter={() => {
        if (!canShow) return;
        updatePosition();
        setIsOpen(true);
      }}
      onMouseLeave={() => setIsOpen(false)}
      onFocus={() => {
        if (!canShow) return;
        updatePosition();
        setIsOpen(true);
      }}
      onBlur={() => setIsOpen(false)}
    >
      {children}
      {portal}
    </span>
  );
};
